import { Autocomplete } from "@base-ui/react/autocomplete"
import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

const BaseAutocomplete = ({
  value,
  onValueChange,
  items = [],
  renderOption,
  itemToStringValue,
  onItemClick,
  isLoading = false,
  placeholder,
  disabled = false,
  autoFocus = false,
  id,
  noResultsContent,
  filter = null,
  inputRef,
  open,
  onOpenChange,
}) => (
  <Autocomplete.Root
    items={items}
    value={value}
    onValueChange={onValueChange}
    itemToStringValue={itemToStringValue}
    filter={filter}
    open={open}
    onOpenChange={onOpenChange}
  >
    <div className="relative">
      <Autocomplete.Input
        ref={inputRef}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        className={classNames(
          "h-10 w-full rounded-sm border px-2 py-3 outline-none focus:border-blue-600",
          { "bg-gray-50 cursor-not-allowed opacity-60": disabled }
        )}
      />
      {isLoading && (
        <span
          data-testid="base-autocomplete-spinner"
          className="icon icon-spinner icon-spin absolute bottom-0 right-3.5 top-0 flex items-center text-gray-400"
        />
      )}
    </div>
    <Autocomplete.Portal>
      <Autocomplete.Positioner
        side="bottom"
        sideOffset={4}
        align="start"
        className="outline-hidden z-modal-stacked"
      >
        <Autocomplete.Popup
          className="overflow-hidden rounded bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          style={{ minWidth: "var(--anchor-width)" }}
        >
          <Autocomplete.Empty>
            <div className="px-2 py-4 text-center text-sm text-gray-500">
              {noResultsContent ?? "No results found"}
            </div>
          </Autocomplete.Empty>
          <Autocomplete.List className="max-h-60 overflow-auto py-1">
            {(item) => (
              <Autocomplete.Item
                key={item?.id}
                value={item}
                onClick={onItemClick ? () => onItemClick(item) : undefined}
                className="flex cursor-pointer items-center px-2 py-2 text-sm text-gray-800 data-[highlighted]:bg-blue-100 data-[highlighted]:outline-none"
              >
                {renderOption
                  ? renderOption({ option: item })
                  : itemToStringValue?.(item) ?? item?.name ?? String(item)}
              </Autocomplete.Item>
            )}
          </Autocomplete.List>
        </Autocomplete.Popup>
      </Autocomplete.Positioner>
    </Autocomplete.Portal>
  </Autocomplete.Root>
)

BaseAutocomplete.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func,
  items: PropTypes.array,
  renderOption: PropTypes.func,
  itemToStringValue: PropTypes.func,
  onItemClick: PropTypes.func,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  id: PropTypes.string,
  noResultsContent: PropTypes.node,
  filter: PropTypes.func,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
}

export default BaseAutocomplete
